import { ActionIcon, Badge, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { HouseholdsExplainer } from 'src/Components/Admin/HouseholdsExplainer';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { useDeleteHousehold } from 'src/Hooks/Mutations/useHouseholdMutations';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchHouseholds from 'src/Hooks/Queries/useFetchHouseholds';
import { AssetGovernanceFields, ShareType } from 'src/types/governance.types';
import { HouseholdCostBearerMode, HouseholdDto } from 'src/types/household.types';
import { OWNERSHIP_FULL_LABEL, OWNERSHIP_MANTINE_COLOR } from 'src/Utils/ownershipColors';

import styles from './HouseholdsSection.module.scss';

const formatPct = (pct: number): string => `${Math.round(pct * 10) / 10}%`;

const HouseholdsSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: asset } = useFetchAssetDetails();
  const { data: households } = useFetchHouseholds();
  const isOwner = asset?.isOwner ?? false;
  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const shareType = governance?.shareType;

  const { mutate: deleteHousehold } = useDeleteHousehold({});

  const handleDelete = (household: HouseholdDto) => {
    if (
      window.confirm(
        t(
          'admin.households.form.deleteConfirm',
          'Supprimer ce foyer ? Les membres rattachés ne seront plus dans aucun foyer.'
        )
      )
    ) {
      deleteHousehold(household.id);
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('admin.households.title', 'Foyers')}
        rightElement={
          isOwner ? (
            <Button
              variant="primary"
              leftSection={<IconPlus size={20} />}
              onClick={() => navigate({ pathname: 'foyers/new', search: '?tab=membres' })}
            >
              {t('admin.households.create', 'Créer un foyer')}
            </Button>
          ) : undefined
        }
      />

      <HouseholdsExplainer />

      <DataTable
        withRowBorders={false}
        style={{ width: '100%', minHeight: '10rem' }}
        records={households}
        onRowClick={
          isOwner
            ? ({ record }) =>
                navigate({ pathname: `foyers/${record.id}/edit`, search: '?tab=membres' })
            : undefined
        }
        noRecordsText={t('admin.households.empty', 'Aucun foyer')}
        columns={[
          {
            accessor: 'name',
            title: t('admin.households.table.name', 'Nom'),
            render: ({ name, color }) => (
              <div className={styles.nameCell}>
                <span className={styles.colorDot} style={{ backgroundColor: color }} />
                <span className={styles.name}>{name}</span>
              </div>
            ),
          },
          {
            accessor: 'members',
            title: t('admin.households.table.members', 'Membres'),
            render: ({ members }) => (
              <Stack gap={2}>
                {members.map(m => (
                  <Group key={m.id} gap={6} wrap="nowrap">
                    <Text size="sm">
                      {[m.firstName, m.lastName].filter(Boolean).join(' ') || '—'}
                    </Text>
                    {m.ownershipNature && (
                      <Badge
                        size="xs"
                        variant="light"
                        color={OWNERSHIP_MANTINE_COLOR[m.ownershipNature]}
                      >
                        {OWNERSHIP_FULL_LABEL[m.ownershipNature]} {formatPct(m.sharesPct ?? 0)}
                      </Badge>
                    )}
                  </Group>
                ))}
              </Stack>
            ),
          },
          {
            accessor: 'ownershipSummary',
            title: t('admin.households.table.parts', 'Parts'),
            render: ({ ownershipSummary }) => {
              if (!ownershipSummary)
                return (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                );
              return (
                <Stack gap={0}>
                  <Text size="sm" fw={500}>
                    {formatPct(ownershipSummary.capitalSharePct)}
                  </Text>
                  {ownershipSummary.capitalShareTantiemes != null &&
                    shareType !== ShareType.PERCENTAGE && (
                      <Text size="xs" c="dimmed">
                        {ownershipSummary.capitalShareTantiemes}{' '}
                        {shareType === ShareType.PARTS_SOCIALES ? 'parts' : 'tantièmes'}
                      </Text>
                    )}
                </Stack>
              );
            },
          },
          {
            accessor: 'head',
            title: t('admin.households.table.head', 'Représentant'),
            render: ({ head }) =>
              head ? (
                <Text size="sm">{[head.firstName, head.lastName].filter(Boolean).join(' ')}</Text>
              ) : (
                <Text size="sm" c="dimmed">
                  —
                </Text>
              ),
          },
          {
            accessor: 'costBearerMode',
            title: t('admin.households.table.costBearer', 'Frais'),
            render: ({ costBearerMode }) => (
              <Badge size="xs" variant="light" color="gray">
                {costBearerMode === HouseholdCostBearerMode.REPRESENTATIVE
                  ? t('admin.households.modeRepresentative', 'Représentant')
                  : t('admin.households.modeOwnersSplit', 'Propriétaires')}
              </Badge>
            ),
          },
          ...(isOwner
            ? [
                {
                  accessor: 'actions',
                  title: '',
                  width: 90,
                  render: (household: HouseholdDto) => (
                    <Group gap={4} wrap="nowrap" justify="flex-end">
                      <Tooltip
                        label={t('admin.households.editTooltip', 'Modifier le foyer')}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={e => {
                            e.stopPropagation();
                            navigate({
                              pathname: `foyers/${household.id}/edit`,
                              search: '?tab=membres',
                            });
                          }}
                          aria-label={t('admin.households.editTooltip', 'Modifier le foyer')}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={t('admin.households.deleteTooltip', 'Supprimer le foyer')}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={e => {
                            e.stopPropagation();
                            handleDelete(household);
                          }}
                          aria-label={t('admin.households.deleteTooltip', 'Supprimer le foyer')}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default HouseholdsSection;
